import { useState, useEffect, useCallback } from 'react';
import { billingClientsService } from '../lib/database';
import { getDataSource } from '../lib/config';
import { writeClientAudit } from '../lib/clientAudit';
import { billingClientAuditSummary } from '../lib/moduleAudit';
import type { BillingClient } from '../types';

interface UseBillingClientsReturn {
  billingClients: BillingClient[];
  loading: boolean;
  error: string | null;
  create: (client: Omit<BillingClient, 'id'>) => Promise<BillingClient>;
  update: (id: number, updates: Partial<BillingClient>) => Promise<void>;
  remove: (id: number) => Promise<void>;
}

export const useBillingClients = (enabled = true): UseBillingClientsReturn => {
  const [billingClients, setBillingClients] = useState<BillingClient[]>([]);
  const [loading, setLoading] = useState(() => enabled && getDataSource() !== 'mock');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    if (getDataSource() === 'mock') {
      setBillingClients([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsub = billingClientsService.subscribe(
      (clients) => {
        setBillingClients(clients.sort((a, b) => a.name.localeCompare(b.name)));
        setLoading(false);
      },
      (err) => {
        setError(err instanceof Error ? err.message : 'Failed to load billing clients');
        setLoading(false);
      },
    );
    return () => unsub();
  }, [enabled]);

  const create = useCallback(async (client: Omit<BillingClient, 'id'>) => {
    const created = await billingClientsService.create(client);
    setBillingClients((prev) =>
      [...prev.filter((c) => c.id !== created.id), created].sort((a, b) => a.name.localeCompare(b.name)),
    );
    writeClientAudit({
      action: 'billing_client.added',
      entityType: 'billing_client',
      entityId: String(created.id),
      summary: billingClientAuditSummary('added', created.name),
    });
    return created;
  }, []);

  const update = useCallback(async (id: number, updates: Partial<BillingClient>) => {
    const previous = billingClients.find((client) => client.id === id);
    await billingClientsService.update(id, updates);
    setBillingClients((prev) =>
      prev.map((c) => (c.id === id ? { ...c, ...updates } : c)).sort((a, b) => a.name.localeCompare(b.name)),
    );
    writeClientAudit({
      action: 'billing_client.updated',
      entityType: 'billing_client',
      entityId: String(id),
      summary: billingClientAuditSummary('saved', updates.name || previous?.name || 'Billing client', previous as unknown as Record<string, unknown>, updates as Record<string, unknown>),
    });
  }, [billingClients]);

  const remove = useCallback(async (id: number) => {
    const previous = billingClients.find((client) => client.id === id);
    await billingClientsService.delete(id);
    setBillingClients((prev) => prev.filter((c) => c.id !== id));
    writeClientAudit({
      action: 'billing_client.removed',
      entityType: 'billing_client',
      entityId: String(id),
      summary: billingClientAuditSummary('removed', previous?.name || 'Billing client'),
    });
  }, [billingClients]);

  return { billingClients, loading, error, create, update, remove };
};
